// ═══════════════════════════════════════════════════════════════════════
// The centre-stage narrative machine (v6) — pure, tested.
//
// CrashMultiplierDisplay owns the one number on screen; this module owns
// WHICH beat of the round it narrates. The continuous read is:
//
//   Place your bets → Tracking armed + 3‥2‥1 → live multiplier →
//   CRASHED @ X (HELD on screen) → Place your bets …
//
// The crash stamp persists until the NEXT round's betting opens
// (race_ready) — including across the race_scheduled gap where the store
// has already rolled to the new race but betting hasn't opened yet.
// ═══════════════════════════════════════════════════════════════════════

export type CrashDisplayPhase = 'hidden' | 'betting' | 'arming' | 'live' | 'crash'

// Crash-curve contract — same number as the 155-game-crash engine, the Go
// backend and useCrashMultiplier. The multiplier sits flat at 1.00x for
// this long after race start (the reveal hold) before climbing.
// Re-exported from the leaf ./crashCurve module (single source of truth) so
// existing importers of this symbol keep working.
export { CRASH_REVEAL_DELAY_SECONDS } from './crashCurve'
import { CRASH_REVEAL_DELAY_SECONDS } from './crashCurve'

// ── Same-round crash latch (v9, defensive) ──────────────────────────────
// HARD INVARIANT: once the display / visual-crash machinery has ENTERED
// crash for a given race uuid, it must be IMPOSSIBLE to re-enter live or
// arming for that SAME uuid. Not on event-list churn (a late race_ready
// replay re-sorting to "latest"), not on beacon oscillation (a frame-clock
// re-read below t_crash), not on the races[] array transiently losing its
// head (uuid → undefined → same uuid). ONLY a genuinely NEW race uuid — a
// defined uuid different from the latched one — releases the latch.
//
// Pure helpers shared by BOTH crash surfaces: useVisualCrash's module store
// (the zone cards / status pill / HUD flip) and CrashMultiplierDisplay's
// phase machine (the hero stamp).

/** Recorded when a crash lands while the store has no race uuid to name. */
export const CRASH_LATCH_UNKNOWN_ROUND = ''

/**
 * Is the latch still holding for the round currently in the store?
 * An undefined uuid is churn noise, never a new round — it keeps holding.
 */
export function crashLatchEngaged(
  latchedRoundId: string | null,
  roundId: string | undefined
): boolean {
  if (latchedRoundId === null) return false
  return roundId === undefined || roundId === latchedRoundId
}

/**
 * The latch value after seeing `roundId`: kept verbatim while engaged,
 * cleared (null) the moment a genuinely new uuid shows up.
 */
export function releaseCrashLatch(
  latchedRoundId: string | null,
  roundId: string | undefined
): string | null {
  return crashLatchEngaged(latchedRoundId, roundId) ? latchedRoundId : null
}

export interface DisplayPhaseInputs {
  // A round is engaged: climbing, or its crash event is known but the
  // viewer's crash frame hasn't necessarily been stamped yet.
  engaged: boolean
  // Betting window open (race_ready / race_starting) — the stage-setter.
  isBettingOpen: boolean
  // resolveCrashHold said STAMP: the crash is on this viewer's frames.
  crashStamped: boolean
  // The ONE display clock (beacon-preferred, event-clock fallback), in
  // race seconds. undefined = no clock available yet.
  tDisplay: number | undefined
  // The phase currently on screen — crash persistence and clock-gap holds
  // resolve against it.
  prevPhase: CrashDisplayPhase
  revealDelaySeconds?: number
}

export function resolveDisplayPhase(i: DisplayPhaseInputs): CrashDisplayPhase {
  const revealDelay = i.revealDelaySeconds ?? CRASH_REVEAL_DELAY_SECONDS

  // The stamp wins over everything — the loop latches it and parks.
  if (i.crashStamped) return 'crash'

  if (!i.engaged) {
    // The next round's betting window opened → the stage-setter takes
    // over (crossfading away a held crash stamp if there was one).
    if (i.isBettingOpen) return 'betting'
    // No live round, no betting: HOLD a crash stamp through the
    // race_scheduled gap (the store already rolled to the next race but
    // betting hasn't opened); otherwise there is nothing to narrate.
    return i.prevPhase === 'crash' ? 'crash' : 'hidden'
  }

  // Engaged but no display clock yet (no beacon lock, no race_started
  // timestamp) — hold what's on screen rather than flicker.
  if (i.tDisplay === undefined) return i.prevPhase

  // Pre-roll footage (the beacon runs negative before the round's frames
  // reach t=0): the stage-setter holds — no blank gap at round start.
  if (i.tDisplay < 0) return 'betting'

  if (i.tDisplay < revealDelay) return 'arming'

  return 'live'
}

/**
 * One-way floor for the ENGAGED phase ladder (review 2026-08-17): once this
 * round's loop has shown 'live', a lower display clock may not regress the
 * narrative to the countdown or the stage-setter. The regression was real:
 * the dead-beacon event-clock fallback engaging (or a decoder re-lock stepping
 * the clock back by the stream lag) crossed the reveal boundary and re-mounted
 * "TRACKING ARMED" mid-climb — republishing a hard 1 that collapsed the
 * cash-out bar to the stake. Routed through the LIVE branch instead, F5
 * (holdMonotonicLiveValue) keeps the published value at the round's shown max.
 *
 * Scope deliberately narrow: only 'live' floors. arming→betting stays free —
 * that is the designed pre-roll stage-setter hold (beacon running negative
 * while the event clock is small positive), which happens every round.
 * A new round releases the floor naturally: its betting window renders via
 * the !engaged path, so prevPhase is no longer 'live' when it re-engages.
 */
export function holdLivePhase(
  candidate: CrashDisplayPhase,
  prevPhase: CrashDisplayPhase
): CrashDisplayPhase {
  if (prevPhase === 'live' && (candidate === 'arming' || candidate === 'betting')) return 'live'
  return candidate
}
